import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, CalendarCheck, MessageCircle, Info } from "lucide-react";
import { CONCERNS, SERVICES, JANE_APP_URL, WHATSAPP } from "../data/siteData";
import { trackEvent } from "../lib/analytics";

const spring = { type: "spring", stiffness: 200, damping: 20 };

export const ConcernGuide = () => {
  const [selectedId, setSelectedId] = useState(null);
  const startedRef = useRef(false);

  const selected = CONCERNS.find((c) => c.id === selectedId) || null;
  const service = selected && selected.service !== "unsure" ? SERVICES.find((s) => s.slug === selected.service) : null;
  const mode = !selected ? "idle" : service ? "service" : "unsure";

  const choose = (id) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackEvent("tool_started", { location: "start_tool" });
    }
    setSelectedId(id);
    const concern = CONCERNS.find((c) => c.id === id);
    trackEvent("concern_selected", { concern: id, service: concern.service });
  };

  useEffect(() => {
    if (selected) {
      trackEvent("tool_recommendation", { recommended: service ? service.slug : "unsure", concern: selected.id });
    }
  }, [selectedId]);

  return (
    <div data-testid="concern-guide" className="max-w-4xl mx-auto">
      <div className="text-center">
        <span className="inline-flex items-center gap-2 bg-lime text-navy font-extrabold text-xs uppercase tracking-[0.16em] px-4 py-2 rounded-full">
          <Compass className="w-3.5 h-3.5" /> Not sure where to start?
        </span>
        <h2 data-testid="concern-guide-title" className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold text-navy tracking-tight">
          Tell us what you're noticing
        </h2>
        <p className="mt-3 text-base sm:text-lg text-navy-light max-w-xl mx-auto">
          Tap the one that sounds most like your child - we'll point you toward a helpful first step.
        </p>
      </div>

      <div data-testid="concern-options" className="mt-10 grid sm:grid-cols-2 gap-3">
        {CONCERNS.map((concern) => {
          const active = selectedId === concern.id;
          return (
            <button
              key={concern.id}
              onClick={() => choose(concern.id)}
              data-testid={`concern-chip-${concern.id}`}
              aria-pressed={active}
              className={`text-left px-5 py-4 rounded-2xl border-2 font-bold text-sm sm:text-base transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2 ${
                active
                  ? "bg-teal text-navy border-teal shadow-lg shadow-teal/20"
                  : "bg-white text-navy border-border hover:border-teal hover:-translate-y-0.5"
              }`}
            >
              {concern.label}
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {mode === "idle" && (
          <motion.p
            key="idle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="concern-guide-idle"
            className="mt-8 text-center text-sm font-bold text-navy-light/70"
          >
            Choose the closest match above - it only takes a moment.
          </motion.p>
        )}

        {mode === "service" && service && (
          <motion.div
            key={service.slug}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={spring}
            data-testid="concern-guide-recommendation"
            className="mt-10 bg-teal-soft border-2 border-teal/40 rounded-[2rem] p-8 text-center"
          >
            <h3 data-testid="concern-guide-service-name" className="font-display text-2xl sm:text-3xl font-semibold text-navy text-balance">
              {service.fullName} may be a great place to start.
            </h3>
            <p data-testid="concern-guide-reason" className="mt-3 text-base text-navy-light max-w-lg mx-auto leading-relaxed">
              {selected.reason}
            </p>
            <div className="mt-7 flex flex-col items-center gap-4">
              {service.slug === "speech-language-therapy" ? (
                <a
                  href={JANE_APP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("book_evaluation_click", { location: "start_tool", service: service.slug, channel: "jane" })}
                  data-testid="concern-guide-book-btn"
                  className="inline-flex items-center gap-2 bg-sunny text-navy font-extrabold px-8 py-4 rounded-full shadow-lg shadow-orange-500/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-sunny-dark focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
                >
                  <CalendarCheck className="w-5 h-5" strokeWidth={2.5} />
                  Book a {service.name} Evaluation
                </a>
              ) : (
                <Link
                  to="/contact"
                  onClick={() => trackEvent("book_evaluation_click", { location: "start_tool", service: service.slug, channel: "contact" })}
                  data-testid="concern-guide-book-btn"
                  className="inline-flex items-center gap-2 bg-sunny text-navy font-extrabold px-8 py-4 rounded-full shadow-lg shadow-orange-500/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-sunny-dark focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
                >
                  <CalendarCheck className="w-5 h-5" strokeWidth={2.5} />
                  Book a {service.name} Evaluation
                </Link>
              )}
              <a
                href={WHATSAPP.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("ask_thrive_click", { location: "start_tool", service: service.slug })}
                data-testid="concern-guide-ask-btn"
                className="inline-flex items-center gap-2 font-extrabold text-sm text-teal-dark hover:underline"
              >
                <MessageCircle className="w-4 h-4" strokeWidth={2.5} />
                Still not sure? Ask Thrive
              </a>
            </div>
          </motion.div>
        )}

        {mode === "unsure" && (
          <motion.div
            key="unsure"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={spring}
            data-testid="concern-guide-recommendation"
            className="mt-10 bg-lilac-soft border-2 border-lilac/50 rounded-[2rem] p-8 text-center"
          >
            <h3 data-testid="concern-guide-unsure-title" className="font-display text-2xl sm:text-3xl font-semibold text-navy text-balance">
              Not sure which service your child needs? That's okay.
            </h3>
            <p className="mt-3 text-base text-navy-light max-w-lg mx-auto leading-relaxed">
              Children's needs can overlap across different areas, and you don't need to figure that out alone. Our team will listen to what you're noticing and help you find the most appropriate starting point.
            </p>
            <div className="mt-7 flex flex-col items-center gap-4">
              <a
                href={WHATSAPP.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackEvent("ask_thrive_click", { location: "start_tool", service: "unsure" })}
                data-testid="concern-guide-ask-btn"
                className="inline-flex items-center gap-2 bg-teal-dark text-white font-extrabold px-8 py-4 rounded-full shadow-lg shadow-teal-dark/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#1C565E] focus:outline-none focus:ring-2 focus:ring-teal focus:ring-offset-2"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
                Ask Thrive
              </a>
              <Link
                to="/contact"
                data-testid="concern-guide-contact-link"
                className="font-extrabold text-sm text-teal-dark hover:underline"
              >
                or send us a message
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <p data-testid="concern-guide-disclaimer" className="mt-6 flex items-center justify-center gap-2 text-xs font-bold text-navy-light/70 text-center">
        <Info className="w-3.5 h-3.5 shrink-0" />
        This guide simply points you in a helpful direction - it isn't a diagnosis.
      </p>
    </div>
  );
};
